import type { AnyFieldApi } from '@tanstack/react-form';
import type { ReactNode } from 'react';
import { Field, SegmentedButtons, type FieldLayout } from '@/shared/ui';

/** Jedna volba segmentovaného přepínače. `value` je uložená hodnota pole (string enum). */
export interface SegmentedFieldOption<T extends string = string> {
  value: T;
  label: ReactNode;
}

interface SegmentedFieldCommon<T extends string> {
  /** TanStack Form field API (z render-prop `form.Field`). */
  field: AnyFieldApi;
  label?: ReactNode;
  helperText?: ReactNode;
  required?: boolean;
  options: ReadonlyArray<SegmentedFieldOption<T>>;
  /** Rozložení labelu (default `'inline'` — label vlevo). */
  layout?: FieldLayout;
  /** Šířka levého labelu v px (jen `layout="inline"`). */
  labelWidth?: number;
  disabled?: boolean;
  testId?: string;
}

/**
 * `multiple` přepne pole na multi-select: hodnota je `T[]` (výběr více voleb), jinak single `T`.
 */
export type SegmentedFieldProps<T extends string = string> =
  | (SegmentedFieldCommon<T> & { multiple?: false })
  | (SegmentedFieldCommon<T> & { multiple: true });

/**
 * Segmentovaný přepínač napojený na TanStack Form. Renderuje sdílené `SegmentedButtons`
 * (aktivní = primary, ostatní = outline); čte/zapisuje `field.state.value`, chybu ukazuje až
 * po dotčení pole (`isTouched`). Single výběr (default) drží hodnotu `T`, `multiple` drží `T[]`.
 */
export function SegmentedField<T extends string = string>(props: SegmentedFieldProps<T>) {
  const {
    field,
    label,
    helperText,
    required,
    options,
    layout = 'inline',
    labelWidth,
    disabled,
    testId,
  } = props;

  const error = field.state.meta.isTouched
    ? (field.state.meta.errors[0]?.message as string | undefined)
    : undefined;

  return (
    <Field
      label={label}
      required={required}
      error={error}
      hint={helperText}
      layout={layout}
      labelWidth={labelWidth}
    >
      <div data-testid={testId}>
        {props.multiple ? (
          <SegmentedButtons<T>
            multiple
            value={(field.state.value as readonly T[] | undefined) ?? []}
            onChange={(next) => field.handleChange(next)}
            options={options}
            disabled={disabled}
          />
        ) : (
          <SegmentedButtons<T>
            value={field.state.value as T | null | undefined}
            onChange={(next) => field.handleChange(next)}
            options={options}
            disabled={disabled}
          />
        )}
      </div>
    </Field>
  );
}
